/**
 * AppShell — the top-bar chrome (2026-07 redesign): section tabs, the active
 * section's sub-row, portfolio switcher, Live toggle (Performance Summary only),
 * command palette and account menu. Access-filtered via lib/access.
 */
import { type CSSProperties, type ReactNode, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LiveToggle } from '@/components/LiveToggle';
import { MobileNav } from '@/components/MobileNav';
import { PortfolioSwitcher } from '@/components/PortfolioSwitcher';
import { AccountMenu } from '@/components/AccountMenu';
import { CommandPalette, openCommandPalette } from '@/components/CommandPalette';
import { RebalanceBadge } from '@/components/RebalanceBadge';
import { AlertsBadge } from '@/components/AlertsBadge';
import { useAccess } from '@/lib/access';
import { HUB_SUBTAB_TABS, NAV_TABS, SECTION_ORDER, resolveTabAccess, showInSidebar } from '@/lib/nav';

interface AppShellProps {
  children: ReactNode;
}

/** The Performance Summary hub's URL-addressable subtabs. While the hub is
 *  active these render in the shell sub-row as underline links (the handoff
 *  recreation's Dividends frame); the hub's own pill strip stays mobile-only. */
const HUB_SUBTABS = [
  { label: 'Summary', to: '/performance-summary' },
  { label: 'Market Calendar', to: '/performance-summary/dividends' },
  { label: 'Market Themes', to: '/performance-summary/market-themes' },
  { label: 'Global Indexes', to: '/performance-summary/global-indexes' },
  { label: 'Watchlist', to: '/performance-summary/watchlist' },
  { label: 'Projections', to: '/performance-summary/projections' },
] as const;

/**
 * AppShell — the top-bar application shell, built to the handoff's "Desktop
 * Shell Anatomy" (elements A–H):
 *   Row 1  : A brand · B section nav · C ⌘K search · D Live · E account
 *   Sub-row: F portfolio switcher · divider · G page sub-nav
 * (H = each page's own subtabs, rendered by the page body.)
 *
 * This is an IA regrouping of the previous grouped sidebar — the same
 * destinations, access filtering (showInSidebar / resolveTabAccess) and routes
 * are preserved. Below md the MobileNav pieces take over.
 */
export function AppShell({ children }: AppShellProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { tabVisibility, userId } = useAccess();
  const isAuthed = !!userId;

  // Sections and their visible entries, filtered by each tab's resolved access
  // (same rule the sidebar used). alwaysShow (Settings) keeps a tab visible to
  // everyone as a "sign in to use" entry point.
  const entriesBySection = useMemo(
    () =>
      SECTION_ORDER.map((section) => ({
        section,
        entries: NAV_TABS.filter(
          (t) =>
            t.section === section &&
            (t.alwaysShow || showInSidebar(resolveTabAccess(t, tabVisibility), isAuthed)),
        ),
      })).filter((s) => s.entries.length > 0),
    [tabVisibility, isAuthed],
  );

  // Which tab (and section) is active? Exact path match, or prefix match for
  // sub-routes like /performance-summary/dividends → the performance-summary tab.
  const activeTab = useMemo(() => {
    const path = location.pathname;
    return (
      NAV_TABS.find((t) => t.to === path) ??
      NAV_TABS.find((t) => path.startsWith(`${t.to}/`))
    );
  }, [location.pathname]);

  const activeSection = activeTab?.section ?? entriesBySection[0]?.section ?? SECTION_ORDER[0];
  const subEntries = entriesBySection.find((s) => s.section === activeSection)?.entries ?? [];
  // Hub subtabs with their own access entry (Market Themes) honor it here too:
  // a 'hidden'/'login' subtab is unlisted for logged-out visitors, same rule
  // as the section nav (finding: the static strip advertised gated content).
  const hubSubtabs = useMemo(
    () => HUB_SUBTABS.filter((st) => {
      const gated = HUB_SUBTAB_TABS.find((t) => t.to === st.to);
      return !gated || showInSidebar(resolveTabAccess(gated, tabVisibility), isAuthed);
    }),
    [tabVisibility, isAuthed],
  );
  // Which subtab is underlined. LONGEST match wins, not first: Summary's path
  // ('/performance-summary') is a prefix of every other subtab's, so a plain
  // startsWith test would underline Summary everywhere. Prefix matching is
  // needed at all because Projections addresses its inner panels by path
  // (/performance-summary/projections/risk) and the underline must survive
  // moving off the landing panel.
  const activeHubSubtab = useMemo(() => {
    const path = location.pathname;
    let best: string | undefined;
    for (const st of HUB_SUBTABS) {
      if (path === st.to || path.startsWith(`${st.to}/`)) {
        if (!best || st.to.length > best.length) best = st.to;
      }
    }
    return best;
  }, [location.pathname]);

  // On the hub, the sub-row leads with its subtabs; the section's remaining
  // pages follow after a divider so every destination stays reachable.
  const onHub = activeTab?.key === 'performanceSummary';
  const pageEntries = onHub ? subEntries.filter((e) => e.key !== 'performanceSummary') : subEntries;

  const goToSection = (section: string) => {
    const first = entriesBySection.find((s) => s.section === section)?.entries[0];
    if (first) navigate(first.to);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ⌘K palette — available on every viewport. */}
      <CommandPalette />
      <MobileNav />

      {/* ── Desktop top bar (≥md) ─────────────────────────────────────────── */}
      <header className="fixed inset-x-0 top-0 z-30 hidden bg-card md:block">
        {/* Row 1 — A brand · B sections · C search · D live · E account.
            56px, matching the handoff recreations exactly. */}
        <div className="flex h-14 items-center gap-[22px] px-5">
          {/* A — brand */}
          <Link
            to="/performance-summary"
            className="flex shrink-0 items-center gap-2.5 no-underline hover:opacity-85"
            aria-label="Portfolio Ledger - go to Performance Summary"
          >
            <span
              aria-hidden="true"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-white"
              style={{
                background: 'linear-gradient(135deg, #0F172A, #2563EB)',
                fontFamily: "'Inter Tight', system-ui, sans-serif",
                fontWeight: 700,
                fontSize: '12.5px',
                letterSpacing: '-0.02em',
              }}
            >
              PL
            </span>
            <span
              className="hidden whitespace-nowrap text-sm font-semibold lg:inline"
              style={{ fontFamily: "'Inter Tight', system-ui, sans-serif", color: 'var(--text)' }}
            >
              Portfolio Ledger
            </span>
          </Link>

          {/* B — section nav */}
          <nav aria-label="Sections" className="flex items-center gap-0.5">
            {entriesBySection.map(({ section }) => {
              const active = section === activeSection;
              return (
                <button
                  key={section}
                  type="button"
                  onClick={() => goToSection(section)}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'rounded-lg px-2.5 py-[7px] text-[12.5px] transition-colors',
                    active
                      ? 'bg-muted font-semibold text-foreground'
                      : 'font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                  )}
                >
                  {section}
                </button>
              );
            })}
          </nav>

          {/* right cluster — C search · D live · E account */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* C — ⌘K search */}
            <button
              type="button"
              onClick={() => openCommandPalette()}
              aria-label="Search or jump to (Command K)"
              className="hidden h-[34px] w-[168px] items-center gap-2 rounded-[9px] border border-border bg-muted/50 px-2.5 text-muted-foreground transition-colors hover:border-primary lg:flex"
            >
              <Search className="h-[15px] w-[15px] shrink-0" />
              <span className="text-[12px]">Search…</span>
              <span className="ml-auto rounded border border-border bg-card px-[5px] py-[3px] font-mono text-[10px] leading-none">
                ⌘K
              </span>
            </button>

            {/* D — live */}
            <LiveToggle />

            {/* E — account */}
            <AccountMenu />
          </div>
        </div>

        {/* Sub-row — F switcher · divider · G page sub-nav (44px, per handoff) */}
        {subEntries.length > 0 && (
          <div className="flex h-11 items-center gap-[18px] border-t border-(--line-soft,var(--border)) px-5">
            <PortfolioSwitcher variant="desktop" />
            <span className="h-5 w-px shrink-0 bg-border" aria-hidden="true" />
            <nav
              aria-label={`${activeSection} pages`}
              className="flex items-center gap-1.5 overflow-x-auto"
            >
              {onHub &&
                hubSubtabs.map((st) => {
                  const active = st.to === activeHubSubtab;
                  return (
                    <Link
                      key={st.to}
                      to={st.to}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'whitespace-nowrap px-2.5 py-[5px] text-[12.5px] no-underline transition-colors',
                        active
                          ? 'border-b-2 border-primary font-semibold text-primary'
                          : 'border-b-2 border-transparent font-medium text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {st.label}
                      {st.to === '/performance-summary/projections' && <RebalanceBadge />}
                      {st.to === '/performance-summary' && <AlertsBadge />}
                    </Link>
                  );
                })}
              {onHub && pageEntries.length > 0 && (
                <span className="mx-2 h-4 w-px shrink-0 bg-border" aria-hidden="true" />
              )}
              {pageEntries.map((entry) => {
                const active = activeTab?.to === entry.to;
                return (
                  <Link
                    key={entry.to}
                    to={entry.to}
                    title={entry.desc}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'whitespace-nowrap px-2.5 py-[5px] text-[12.5px] no-underline transition-colors',
                      active
                        ? 'border-b-2 border-primary font-semibold text-primary'
                        : 'border-b-2 border-transparent font-medium text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {entry.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}

        {/* hairline under the whole bar */}
        <div className="h-px w-full bg-(--line-soft,var(--border))" aria-hidden="true" />
      </header>

      {/* overflow-x must be clip, not hidden: hidden would make <main> the scroll
          context for sticky descendants (frozen table headers would pin to
          <main>, which never scrolls). clip clips without a scroll container. */}
      <main
        className="app-main overflow-x-clip"
        // The recreation's page eyebrow (mono uppercase section name above the
        // page title) is injected purely via CSS — .page-title::before reads
        // this var, so no page component changes are needed.
        style={{ '--page-eyebrow': `'${activeSection}'` } as CSSProperties}
      >
        <div className="w-full px-4 pt-[calc(56px+env(safe-area-inset-top,0px)+0.75rem)] pb-[calc(64px+env(safe-area-inset-bottom,0px)+0.75rem)] md:px-8 md:pb-8 md:pt-[116px]">
          {children}
        </div>
      </main>
    </div>
  );
}
